"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, Clock, ArrowRight, BookOpen } from "lucide-react";
const getReadingTime = (content?: string) => {
  if (!content) return 5;
  const words = content.replace(/<[^>]*>?/gm, '').split(/\s+/).length;
  return Math.max(1, Math.ceil(words / 200));
};
const formatDate = (dateString?: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric"
  });
};
function KnowledgeBaseConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7">
          <Card className="h-[600px] animate-pulse bg-muted/30 border-none" />
        </div>
        <div className="lg:col-span-5 flex flex-col gap-6">
          {[...Array(3)].map((_, i) => <Card key={i} className="h-40 animate-pulse bg-muted/30 border-none" data-index={i} />)}
        </div>
      </div>;
  }
  if (error) {
    return <div className="text-center py-12 bg-destructive/10 rounded-2xl">
        <p className="text-destructive font-medium">Unable to load news. Please try again.</p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="text-center py-12 bg-muted/20 rounded-2xl">
        <p className="text-muted-foreground">No news published yet.</p>
      </div>;
  }
  const featuredPost = posts[0];
  const listPosts = posts.slice(1, 4);
  return <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      {}
      <div className="lg:col-span-7 flex flex-col h-full">
        <Link to={featuredPost.link || "#"} className="group flex-1 flex flex-col">
          <Card className="flex-1 flex flex-col overflow-hidden border-border/40 shadow-sm transition-all duration-300 group-hover:shadow-xl group-hover:border-primary/20 p-0 gap-0 rounded-2xl bg-card">
            <div className="relative w-full aspect-[16/10] overflow-hidden bg-muted">
              {featuredPost.featuredImage ? <img src={featuredPost.featuredImage} alt={featuredPost.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" /> : <div className="w-full h-full flex items-center justify-center bg-secondary/50 text-muted-foreground">
                  <BookOpen className="w-16 h-16 opacity-20" />
                </div>}
              {featuredPost.categories && featuredPost.categories.length > 0 && <div className="absolute top-4 left-4">
                  <span className="bg-primary text-primary-foreground text-xs font-bold uppercase tracking-widest py-1.5 px-4 shadow-md">
                    {featuredPost.categories[0].name}
                  </span>
                </div>}
            </div>
            
            <CardContent className="p-8 flex flex-col flex-1 justify-between">
              <div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                  <div className="flex items-center gap-1.5">
                    <SafeHydrate><Calendar className="w-4 h-4 text-ring" /></SafeHydrate>
                    <span>{formatDate(featuredPost.date)}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-ring" />
                    <span>{getReadingTime(featuredPost.content)} min read</span>
                  </div>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold font-default text-foreground leading-tight mb-4 group-hover:text-ring transition-colors">
                  {featuredPost.title}
                </h3>
                {featuredPost.excerpt && <div className="text-muted-foreground text-base line-clamp-3 leading-relaxed font-default" dangerouslySetInnerHTML={{
                __html: featuredPost.excerpt
              }} />}
              </div>
              
              <div className="mt-8 flex items-center text-primary font-bold tracking-wider uppercase text-sm">
                Read More
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-2" />
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {}
      <div className="lg:col-span-5 flex flex-col gap-6">
        {listPosts.map((post, i) => <Link key={post.id} to={post.link || "#"} className="group block h-full" data-index={i}>
            <Card className="h-full flex flex-col sm:flex-row overflow-hidden border-border/40 shadow-sm transition-all duration-300 group-hover:shadow-lg group-hover:border-primary/20 p-0 gap-0 rounded-2xl bg-card">
              <div className="relative w-full sm:w-2/5 aspect-video sm:aspect-auto sm:h-full overflow-hidden bg-muted shrink-0">
                {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" /> : <div className="w-full h-full flex items-center justify-center bg-secondary/50 text-muted-foreground min-h-[160px]">
                    <BookOpen className="w-8 h-8 opacity-20" />
                  </div>}
              </div>
              
              <CardContent className="p-5 flex flex-col justify-center flex-1">
                {post.categories && post.categories.length > 0 && <span className="text-ring text-xs font-bold uppercase tracking-widest mb-2 block">
                    {post.categories[0].name}
                  </span>}
                <h4 className="text-lg font-bold font-default text-foreground leading-snug mb-3 group-hover:text-ring transition-colors line-clamp-2">
                  {post.title}
                </h4>
                
                <div className="flex items-center gap-4 text-xs text-muted-foreground mt-auto pt-2">
                  <div className="flex items-center gap-1">
                    <SafeHydrate><Calendar className="w-3.5 h-3.5" /></SafeHydrate>
                    <span>{formatDate(post.date)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    <span>{getReadingTime(post.content)} min read</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>)}
      </div>
    </div>;
}
export default function EduFutureKnowledgeBase() {
  const wp_query_params = {
    post_type: "post",
    per_page: 4,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="222712" id="新闻动态" className="bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[500px] h-[500px] bg-primary/5 rounded-lg blur-3xl pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground tracking-tight mb-4 uppercase">
              News & Insights
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed font-default">
              Explore global investment trends and stay updated on STI Group's latest developments and industry expansion.
            </p>
          </div>
          <Link to="/NewsPage" className="shrink-0">
            
          </Link>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 4,
        orderby: "date",
        order: "desc"
      }}>
          <KnowledgeBaseConsumer />
        </PostsProvider>
      </div>
    </section>;
}